import { useEffect, useRef, type KeyboardEvent, type ReactNode } from 'react'

import { DETAIL_TABS, DETAIL_TAB_LABELS, type DetailTab } from './params'

export const tabId = (tab: DetailTab) => `cd-tab-${tab}`
export const panelId = (tab: DetailTab) => `cd-panel-${tab}`

/**
 * The page's tabs: an ARIA tablist, not navigation (they switch a panel of one page, and
 * `?tab=` keeps the choice in the URL). Only the selected tab is in the Tab order (a
 * roving tabindex); the arrow keys, Home and End move focus along the list, and Enter
 * or Space opens the focused tab. Opening is manual rather than on focus because a tab's
 * panel reads its own route when it opens: arrowing past Funnel to reach Timeline must
 * not ask for the funnel on the way.
 */
export function DetailTabs({ active, onSelect }: { active: DetailTab; onSelect: (tab: DetailTab) => void }) {
  const refs = useRef(new Map<DetailTab, HTMLButtonElement>())

  // On a phone the list scrolls sideways inside itself: bring the open tab (one a link named, say)
  // into it, scrolling only the list, never the page.
  useEffect(() => {
    const tab = refs.current.get(active)
    const list = tab?.parentElement
    if (!tab || !list) return
    const start = tab.offsetLeft - list.offsetLeft
    if (start < list.scrollLeft) list.scrollLeft = start
    else if (start + tab.offsetWidth > list.scrollLeft + list.clientWidth) list.scrollLeft = start + tab.offsetWidth - list.clientWidth
  }, [active])

  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    const last = DETAIL_TABS.length - 1
    const moves: Record<string, number> = {
      ArrowRight: index === last ? 0 : index + 1,
      ArrowLeft: index === 0 ? last : index - 1,
      Home: 0,
      End: last,
    }
    const target = moves[event.key]
    if (target === undefined) return
    event.preventDefault()
    const tab = DETAIL_TABS[target]
    if (tab) refs.current.get(tab)?.focus()
  }

  return (
    <div className="cd-tabs" role="tablist" aria-label="Competition">
      {DETAIL_TABS.map((tab, index) => {
        const selected = tab === active
        return (
          <button
            key={tab}
            ref={(element) => {
              if (element) refs.current.set(tab, element)
              else refs.current.delete(tab)
            }}
            type="button"
            role="tab"
            id={tabId(tab)}
            // Only the open tab's panel is rendered, so only it is named as controlled.
            aria-controls={selected ? panelId(tab) : undefined}
            aria-selected={selected}
            tabIndex={selected ? 0 : -1}
            className="cd-tabs__tab"
            onClick={() => onSelect(tab)}
            onKeyDown={(event) => onKeyDown(event, index)}
          >
            {DETAIL_TAB_LABELS[tab]}
          </button>
        )
      })}
    </div>
  )
}

/** The open tab's panel, named by its tab and reachable with Tab from it. */
export function TabPanel({ tab, children }: { tab: DetailTab; children: ReactNode }) {
  return (
    <div role="tabpanel" id={panelId(tab)} aria-labelledby={tabId(tab)} tabIndex={0} className="cd-panel">
      {children}
    </div>
  )
}
